import React, { useContext, useEffect, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
} from "@mui/material";
import CommonTable from "../components/CommonTable";
import { useConfirm } from "../context/ConfirmContext";
import { useMessage } from "../context/MessageContext";
import {
  buildCatalogItemOptions,
  displayText,
  resolveCatalogLineLabel,
} from "../utils/catalogOptionUtils";

const StockReceipts = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { confirm } = useConfirm();
  const { showMessage } = useMessage();

  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [requests, setRequests] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    items: [],
    deliveryNote: "",
    batchInfo: "",
    receivedAt: new Date().toISOString().substring(0, 16),
  });
  const [itemEntry, setItemEntry] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsOptions, setItemsOptions] = useState([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(
          `/vendors?orgId=${organization}&academicYear=${academicYear}`
        );
        setVendors(res.data);
        // if (res.data.length && !vendor) setVendor(res.data[0]._id);
      } catch {
        showMessage("Failed to load vendors", "error");
      }
    };
    if (organization && academicYear) fetchVendors();
  }, [organization, academicYear]);

  useEffect(() => {
    const fetchCatalog = async () => {
      if (!vendor) {
        setCatalog(null);
        return;
      }
      setCatalog(null);
      try {
        const res = await axios.get(
          `/catalogs?vendorId=${vendor}&academicYear=${academicYear}`
        );
        setCatalog(res.data || null);
      } catch {
        showMessage("Failed to load catalog", "error");
        setCatalog(null);
      }
    };
    fetchCatalog();
  }, [vendor, academicYear]);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!vendor) return;
      try {
        const res = await axios.get(
          `/procurement/requests?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
        );
        setRequests(res.data || []);
      } catch {
        showMessage("Failed to load requests", "error");
      }
    };
    fetchRequests();
  }, [vendor, academicYear, organization]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/procurement/receipts?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
      );
      setReceipts(res.data || []);
    } catch {
      showMessage("Failed to load receipts", "error");
    }
    setLoading(false);
  };
  useEffect(() => {
    if (vendor && organization && academicYear) fetchReceipts();
  }, [vendor, organization, academicYear]);

  useEffect(() => {
    setItemsOptions(buildCatalogItemOptions(catalog));
  }, [catalog]);

  useEffect(() => {
    setItemEntry(null);
    setSelectedItems([]);
    setOpen(false);
  }, [vendor]);

  const formatLocalDateTime = (d) => {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    const h = String(dt.getHours()).padStart(2, "0");
    const min = String(dt.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${day} ${h}:${min}`;
  };

  const handleOpen = () => {
    setForm({
      items: [],
      deliveryNote: "",
      batchInfo: "",
      receivedAt: formatLocalDateTime(new Date()),
    });
    setSelectedItems([]);
    setItemEntry(null);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!selectedItems.length)
      return showMessage("Add at least one item/collection", "warning");
    // if (!form?.deliveryNote)
    //   return showMessage("Add delivery reference", "warning");
    const payload = selectedItems.map(({ id, isCollection, quantity }) => ({
      itemId: id,
      isCollection,
      quantity,
    }));
    try {
      await axios.post("/procurement/receipts", {
        ...form,
        organization,
        vendor,
        academicYear,
        catalog: catalog?._id,
        items: payload,
        receivedAt: form.receivedAt,
      });
      setOpen(false);
      fetchReceipts();
      showMessage("Stock receipt added", "success");
    } catch {
      showMessage("Failed to save receipt", "error");
    }
  };

  const handleCancelReceipt = async (receipt) => {
    const { confirmed, reason } = await confirm({
      title: "Cancel Received Stock",
      message: `Are you sure you want to cancel ${receipt.receiptNumber}? This will revert inventory stock added and stock requests.`,
      requireReason: true,
    });
    if (!confirmed) return;

    try {
      await axios.post(`/procurement/receipts/${receipt._id}/cancel`, {
        reason,
      });
      fetchReceipts();
      showMessage("Receipt cancelled successfully", "success");
    } catch (err) {
      showMessage("Failed to cancel receipt", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Stock Received
      </Typography>
      <FormControl sx={{ mb: 2, minWidth: 250 }}>
        <InputLabel>Vendor</InputLabel>
        <Select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          label="Vendor"
        >
          {vendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{ ml: 2, mb: 2 }}
        disabled={!vendor}
      >
        Add Received Stock
      </Button>
      <CommonTable
        rows={receipts}
        loading={loading}
        columns={[
          { label: "Receipt ID", field: "receiptNumber" },

          // {
          //   label: "Request Fulfillments",
          //   renderCell: (rec) => {
          //     if (!rec.items?.length) return "-";

          //     return (
          //       <Box sx={{ whiteSpace: "pre-line" }}>
          //         {rec.items
          //           .map((item) => {
          //             if (!item.fulfillments?.length) return null;
          //             return item.fulfillments
          //               .map(
          //                 (f) =>
          //                   `Item ${item.itemId} → ${f.quantity} from ${f.requestId}`
          //               )
          //               .join("\n");
          //           })
          //           .filter(Boolean)
          //           .join("\n")}
          //       </Box>
          //     );
          //   },
          // },
          {
            label: "Received At",
            getSearchValue: (rec) => rec.receivedAt || "",
            renderCell: (rec) =>
              rec.receivedAt ? formatLocalDateTime(rec.receivedAt) : "-",
            getExportValue: (rec) => formatLocalDateTime(rec.receivedAt) || "",
          },

          {
            label: "Items/Collections (Qty)",
            getSearchValue: (rec) =>
              (rec.items || [])
                .map((it) => {
                  const label = resolveCatalogLineLabel(
                    it.itemId,
                    it.isCollection,
                    itemsOptions
                  );
                  return `${label} ${it.quantity}`;
                })
                .join(" "),
            renderCell: (rec) => (
              <Box sx={{ whiteSpace: "pre-line" }}>
                {(rec.items || [])
                  .map((it) => {
                    const label = resolveCatalogLineLabel(
                      it.itemId,
                      it.isCollection,
                      itemsOptions
                    );
                    return `${label} (${it.quantity})`;
                  })
                  .join("\n") || "-"}
              </Box>
            ),
            getExportValue: (rec) =>
              (rec.items || [])
                .map((it) => {
                  const label = resolveCatalogLineLabel(
                    it.itemId,
                    it.isCollection,
                    itemsOptions
                  );
                  return `${label} (${it.quantity})`;
                })
                .join(", "),
          },
          {
            label: "Delivery Note (Batch Info)",
            getSearchValue: (rec) =>
              `${rec.deliveryNote || ""} ${rec.batchInfo || ""}`.trim(),
            renderCell: (rec) => {
              const deliveryNote = rec.deliveryNote || "";
              const batchInfo = rec.batchInfo || "";
              if (deliveryNote && batchInfo) {
                return `${deliveryNote} (${batchInfo})`;
              } else if (deliveryNote) {
                return deliveryNote;
              } else {
                return "-";
              }
            },
            getExportValue: (rec) => {
              const deliveryNote = rec.deliveryNote || "";
              const batchInfo = rec.batchInfo || "";
              if (deliveryNote && batchInfo) {
                return `${deliveryNote} (${batchInfo})`;
              } else if (deliveryNote) {
                return deliveryNote;
              } else {
                return "-";
              }
            },
          },
          {
            label: "Action",
            renderCell: (rec) =>
              rec.status !== "Cancelled" ? (
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  onClick={() => handleCancelReceipt(rec)}
                >
                  Cancel Receipt
                </Button>
              ) : (
                <Chip onClick={() => {}} label={"Cancelled"} color={"error"} />
              ),
          },
        ]}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Add Stock Receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Items will be auto-matched to pending stock requests based on oldest
            request first.
          </Typography>
          <TextField
            label="Received At"
            type="datetime-local"
            value={form.receivedAt}
            onChange={(e) =>
              setForm((f) => ({ ...f, receivedAt: e.target.value }))
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Delivery Note"
            value={form.deliveryNote}
            onChange={(e) =>
              setForm((f) => ({ ...f, deliveryNote: e.target.value }))
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Batch Info"
            value={form.batchInfo}
            onChange={(e) =>
              setForm((f) => ({ ...f, batchInfo: e.target.value }))
            }
            fullWidth
            margin="normal"
          />
          <Typography sx={{ mt: 2, mb: 1 }}>Items/Collections</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Autocomplete
              options={itemsOptions.filter(
                (opt) => !selectedItems.some((sel) => sel.id === opt.id)
              )}
              getOptionLabel={(opt) => displayText(opt?.label, "Unknown")}
              value={itemEntry}
              onChange={(e, v) =>
                setItemEntry(v ? { ...v, quantity: 1 } : null)
              }
              renderInput={(params) => <TextField {...params} label="Select" />}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Qty"
              type="number"
              value={itemEntry?.quantity || 1}
              onChange={(e) => {
                const q = Math.max(1, parseInt(e.target.value, 10) || 1);
                setItemEntry((entry) =>
                  entry ? { ...entry, quantity: q } : entry
                );
              }}
              sx={{ width: 90 }}
              disabled={!itemEntry}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (itemEntry) {
                  setSelectedItems([...selectedItems, itemEntry]);
                  setItemEntry(null);
                }
              }}
              disabled={!itemEntry}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            {selectedItems.map((item, idx) => (
              <Box
                key={item.id}
                sx={{ display: "flex", alignItems: "center", mt: 1 }}
              >
                <Chip label={displayText(item.label, "Item")} sx={{ mr: 1 }} />
                <Typography sx={{ mr: 1 }}>Qty: {item.quantity}</Typography>
                <Button
                  size="small"
                  onClick={() =>
                    setSelectedItems((list) => list.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockReceipts;
